// 1.5.15: Uses shared/api client for BFF/legacy routing
import { Restaurant } from '../../features/search/types/restaurant';
import { resolveUrl, buildHeaders, buildPhotoUrl } from '../api/client';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const PLACES_API_BASE_URL = 'https://maps.googleapis.com/maps/api/place';

// Budget mapping for Google Places price_level (0-4)
const budgetToPriceLevel = {
  low: [0, 1],
  mid: [2],
  high: [3, 4],
};

// 1.3.8: Google Places API (New) 类型映射
// 根据官方文档：https://developers.google.com/maps/documentation/places/web-service/place-types
// 表 A 中的类型可以在请求中使用，表 B 中的类型只能在响应中返回
// yakiniku_restaurant 和 izakaya 不在表 A 中，需要映射到支持的类型
function mapToSupportedPlaceType(type: string): string {
  const typeMapping: Record<string, string> = {
    // 日本特定类型映射（这些类型不在 Google Places API (New) 的表 A 中）
    'yakiniku_restaurant': 'japanese_restaurant', // 烤肉店 -> 日本料理（表 A 支持）
    'izakaya': 'bar', // 居酒屋 -> 酒吧（表 A 支持）
    'ramen_restaurant': 'japanese_restaurant', // 拉面店 -> 日本料理
    'sushi_restaurant': 'japanese_restaurant', // 寿司店 -> 日本料理
    'shabu_shabu_restaurant': 'japanese_restaurant', // 涮涮锅 -> 日本料理
  };
  
  // 如果类型在映射表中，返回映射后的类型；否则直接返回（可能是已支持的类型）
  return typeMapping[type] || type;
}

// Comprehensive cuisine type mapping to Google Places types and keywords
// Covers Japanese, Chinese, Italian, Indian, Vietnamese, and more
const cuisineTypeMapping: Record<string, string> = {
  // Japanese Cuisine
  '日本料理': 'japanese_restaurant',
  '日餐': 'japanese_restaurant',
  '拉面': 'ramen_restaurant',
  '寿司': 'sushi_restaurant',
  '居酒屋': 'bar', // 1.3.8: 修复 - 居酒屋映射到 bar（Google Places API (New) 不支持 izakaya）
  '天妇罗': 'japanese_restaurant',
  '烤肉': 'japanese_restaurant', // 1.3.8: 修复 - 烤肉映射到 japanese_restaurant（Google Places API (New) 不支持 yakiniku_restaurant）
  '怀石料理': 'kaiseki',
  '荞麦面': 'soba',
  '乌冬面': 'udon',
  
  // Chinese Cuisine
  '中餐': 'chinese_restaurant',
  '中华料理': 'chinese_restaurant',
  '四川菜': 'szechuan chinese_restaurant',
  '粤菜': 'cantonese chinese_restaurant',
  '点心': 'dim_sum chinese_restaurant',
  
  // Italian Cuisine
  '意大利菜': 'italian_restaurant',
  '意餐': 'italian_restaurant',
  '披萨': 'pizza_restaurant',
  '意面': 'pasta italian_restaurant',
  
  // Indian Cuisine
  '印度菜': 'indian_restaurant',
  '印餐': 'indian_restaurant',
  '咖喱': 'curry indian_restaurant',
  '坦都里': 'tandoori indian_restaurant',
  
  // Vietnamese Cuisine
  '越南菜': 'vietnamese_restaurant',
  '越餐': 'vietnamese_restaurant',
  '河粉': 'pho vietnamese_restaurant',
  
  // French Cuisine
  '法国菜': 'french_restaurant',
  '法餐': 'french_restaurant',
  
  // Korean Cuisine
  '韩国菜': 'korean_restaurant',
  '韩餐': 'korean_restaurant',
  '韩式烤肉': 'korean_bbq',
  
  // Thai Cuisine
  '泰国菜': 'thai_restaurant',
  '泰餐': 'thai_restaurant',
  
  // Other Cuisines
  '西餐': 'restaurant',
  '美式': 'american_restaurant',
  '墨西哥': 'mexican_restaurant',
  '西班牙': 'spanish_restaurant',
};

export interface PlacesSearchParams {
  location?: string; // lat,lng format or place name
  radius?: number; // in meters (default: 5000)
  keyword?: string;
  type?: string;
  budget?: 'low' | 'mid' | 'high';
}

interface GooglePlace {
  place_id: string;
  name: string;
  rating?: number;
  price_level?: number;
  vicinity?: string;
  photos?: Array<{ 
    photo_reference: string;
    name?: string; // New API format
    width?: number;
    height?: number;
  }>;
  types?: string[];
  formatted_address?: string;
  international_phone_number?: string;
  user_ratings_total?: number; // New field from new API
  website?: string; // New field from new API
  business_status?: string; // New field from new API
  opening_hours?: {
    open_now?: boolean; // New field from new API
    weekday_text?: string[];
    periods?: Array<{
      open?: { day: number; time: string };
      close?: { day: number; time: string };
    }>;
  };
  geometry?: {
    location: {
      lat: number;
      lng: number;
    };
  };
  url?: string; // Google Maps URL
}


/**
 * Convert Google Place types to cuisine display name
 * 直接使用 Google API 的 types 字段，不做自定义映射
 */
function getCuisineFromTypes(types: string[] | undefined): string {
  if (!types || types.length === 0) {
    return 'Restaurant';
  }

  // 过滤掉通用类型，保留最具体的餐厅类型
  const genericTypes = ['establishment', 'point_of_interest', 'food', 'restaurant'];
  const nonGenericTypes = types.filter(t => !genericTypes.includes(t));
  
  if (nonGenericTypes.length > 0) {
    // 直接使用 Google API 的原始类型，格式化显示（下划线转空格，首字母大写）
    const primaryType = nonGenericTypes[0];
    return primaryType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  // 如果没有具体类型，返回通用类型
  return 'Restaurant';
}

/**
 * Convert Google Place data to Restaurant interface
 * 直接使用 Google API 返回的数据
 */
function convertPlaceToRestaurant(place: GooglePlace, searchKeyword?: string): Restaurant {
  // 直接使用 Google API 的 types 字段，不做自定义映射
  const cuisine = getCuisineFromTypes(place.types);
  
  // Debug logging (only in development)
  if (import.meta.env.DEV) {
    console.log(`🔍 从 Google API 提取数据:`, {
      name: place.name,
      cuisine,
      types: place.types,
      rating: place.rating,
      priceLevel: place.price_level,
      url: place.url,
      hasUrl: !!place.url
    });
  }

  // 直接使用 Google API 的 price_level
  // Google Places price_level: 0=free, 1=inexpensive, 2=moderate, 3=expensive, 4=very expensive
  const priceLevel = place.price_level;
  let budget: 'low' | 'mid' | 'high' = 'mid';
  let priceRange: string;

  // 根据 Google API 的 price_level 直接映射
  if (priceLevel === undefined || priceLevel === null) {
    // 如果 price_level 不可用，使用默认值
    budget = 'mid';
    priceRange = '价格未提供';
  } else if (priceLevel <= 1) {
    budget = 'low';
    priceRange = priceLevel === 0 ? '免费' : '¥500-¥1,500';
  } else if (priceLevel === 2) {
    budget = 'mid';
    priceRange = '¥1,500-¥3,000';
  } else if (priceLevel === 3) {
    budget = 'high';
    priceRange = '¥3,000-¥6,000';
  } else if (priceLevel >= 4) {
    budget = 'high';
    priceRange = '¥6,000+';
  } else {
    budget = 'mid';
    priceRange = '¥1,500-¥3,000';
  }

  // Generate image URL from photo_reference via backend proxy
  // Backend handles both old API format (photo_reference) and new API format (name)
  let imageUrl = '/assets/ramen.jpg'; // default fallback
  if (place.photos && place.photos.length > 0) {
    // Use photo_reference if available, otherwise use name (new API format)
    const photoRef = place.photos[0].photo_reference || place.photos[0].name;
    if (photoRef) {
      // NOTE: Do NOT use encodeURIComponent here — buildPhotoUrl uses
      // URLSearchParams which already encodes values correctly.
      // Double-encoding breaks new-API photo references that contain slashes.
      imageUrl = buildPhotoUrl({ photo_reference: photoRef, maxwidth: '800' });
      if (import.meta.env.DEV) {
        console.log('Generated image URL:', {
          placeName: place.name,
          photoRef,
          imageUrl,
          hasPhotos: !!place.photos,
          photoCount: place.photos?.length || 0,
          isNewApiFormat: place.photos[0].name !== undefined
        });
      }
    }
  } else {
    if (import.meta.env.DEV) {
      console.warn('No photos available for place:', {
        placeName: place.name,
        placeId: place.place_id,
        hasPhotos: !!place.photos
      });
    }
  }

  const tags: string[] = [];
  if (place.rating && place.rating >= 4.5) tags.push('高评分');
  if (priceLevel <= 1) tags.push('实惠');
  if (place.types?.includes('meal_delivery')) tags.push('外送');
  // Add business status tag if available
  if (place.business_status === 'CLOSED_TEMPORARILY') tags.push('临时关闭');
  if (place.opening_hours?.open_now) tags.push('营业中');

  // 直接使用 Google API 的 rating
  // 如果没有 rating，使用 null 或 0，让 UI 决定如何显示
  const rating = place.rating ?? null;

  // 构建 Google Maps URL
  // 优先使用 API 返回的 url，如果没有则手动构建
  let googleMapsUrl = place.url;
  if (!googleMapsUrl && place.place_id) {
    // 使用 place_id 构建 Google Maps URL
    googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${place.place_id}`;
  } else if (!googleMapsUrl && place.geometry?.location) {
    // 如果没有 place_id，使用坐标构建 URL
    const { lat, lng } = place.geometry.location;
    googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  } else if (!googleMapsUrl && (place.formatted_address || place.vicinity)) {
    // 最后使用地址构建 URL
    const address = encodeURIComponent(place.formatted_address || place.vicinity || '');
    googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${address}`;
  }

  const restaurant = {
    id: place.place_id,
    name: place.name, // 保持现状：使用 Google API 的 name
    nameZh: undefined, // 中文名称，如果 AI 服务或 Google API 提供，可以在这里设置
    nameJa: place.name,
    cuisine, // 直接使用 Google API 的 types 字段（不做自定义映射）
    rating: rating ?? 0, // 使用 Google API 的 rating
    budget, // 使用 Google API 的 price_level 转换的 budget
    priceRange, // 使用 Google API 的 price_level 转换的 priceRange
    description: place.vicinity || place.formatted_address || '', // 保持现状：使用 Google API 的地址
    descriptionJa: place.vicinity || place.formatted_address || '',
    image: imageUrl, // 保持现状：使用 Google API 的 photos
    tags,
    address: place.formatted_address || place.vicinity, // 保持现状：使用 Google API 的地址
    addressJa: place.formatted_address || place.vicinity,
    phone: place.international_phone_number,
    openHours: place.opening_hours?.weekday_text?.join('\n'),
    url: googleMapsUrl, // Google Maps URL (优先使用 API 返回的，否则手动构建)
  };
  
  // Debug logging (only in development)
  if (import.meta.env.DEV) {
    console.log(`✅ 餐厅数据转换完成:`, {
      name: restaurant.name,
      cuisine: restaurant.cuisine,
      rating: restaurant.rating,
      priceLevel: place.price_level,
      budget: restaurant.budget,
      priceRange: restaurant.priceRange,
      address: restaurant.address,
      url: restaurant.url,
      urlSource: place.url ? 'API' : (place.place_id ? 'place_id' : place.geometry?.location ? 'coordinates' : 'address'),
      hasImage: !!restaurant.image
    });
  }
  
  return restaurant;
}

/**
 * Search restaurants using Google Places Text Search via Backend Proxy
 */
export async function searchRestaurants(
  params: PlacesSearchParams
): Promise<Restaurant[]> {
  try {
    // Default to Tokyo, Japan if no location specified
    // Support for major cities: Tokyo (default), Osaka
    const location = params.location || '35.6762,139.6503'; // Tokyo coordinates
    const radius = params.radius || 5000;
    
    // Build search query - support all cuisine types
    let query = params.keyword || 'restaurant';
    if (params.type) {
      const mappedType = cuisineTypeMapping[params.type] || params.type;
      query += ` ${mappedType}`;
    }
    // Focus on Japan regions, primarily Tokyo and Osaka
    query += ' Japan'; // Use English for better Google Places API compatibility

    // Call backend proxy (via BFF or direct Edge Function)
    const url = resolveUrl('maps-proxy', '/text-search');
    const headers = buildHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        location,
        radius,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (import.meta.env.DEV) {
        console.error('Backend proxy error:', response.status, errorData);
      }
      throw new Error(`Backend proxy error: ${response.status}`);
    }

    const data = await response.json();

    if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
      if (import.meta.env.DEV) {
        console.error('Google Places API error:', data.status, data.error_message);
      }
      throw new Error(`API_ERROR: ${data.status}`);
    }

    if (!data.results || data.results.length === 0) {
      return [];
    }

    // Convert places to Restaurant format
    // Pass search keyword to help with cuisine detection
    const searchKeyword = params.keyword || '';
    let restaurants = data.results.map(place => convertPlaceToRestaurant(place, searchKeyword));

    // Filter by budget if specified
    if (params.budget) {
      const allowedPriceLevels = budgetToPriceLevel[params.budget];
      restaurants = restaurants.filter((r) =>
        allowedPriceLevels.includes(r.budget === 'low' ? 1 : r.budget === 'high' ? 3 : 2)
      );
    }

    return restaurants.slice(0, 20); // Limit to 20 results
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching from Google Places:', error);
    }
    throw error;
  }
}

/**
 * Get restaurant details by place_id via Backend Proxy
 */
export async function getRestaurantDetails(placeId: string): Promise<Restaurant | null> {
  try {
    const url = resolveUrl('maps-proxy', '/place-details');
    const headers = buildHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ placeId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (import.meta.env.DEV) {
        console.error('Backend proxy error:', response.status, errorData);
      }
      throw new Error(`Backend proxy error: ${response.status}`);
    }

    const data = await response.json();

    if (data.status !== 'OK') {
      if (import.meta.env.DEV) {
        console.error('Google Places API error:', data.status);
      }
      return null;
    }

    return convertPlaceToRestaurant(data.result);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching place details:', error);
    }
    return null;
  }
}
